import { useCallback, useEffect, useState } from 'react';
import { Outlet, useMatches, useNavigate } from 'react-router';
import { cn } from '@/lib/cn';
import { useBreakpoint, useIsAtLeast } from '@/hooks/useBreakpoint';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useAuth } from '@/hooks/useAuth';
import { useHydration } from '@/hooks/useHydration';
import { useUIStore } from '@/stores/uiStore';
import { useCapture } from '@/features/capture/useCapture';
import { useDayBuilder } from '@/features/ai/useDayBuilder';
import { initDeepLinks } from '@/services/DeepLinkService';
import { BraindayError } from '@/services/BraindayAPI';
import { Sidebar } from './Sidebar';
import { BottomTabs } from './BottomTabs';
import { Narrator } from './Narrator';
import { CommandBar, dayBuilderErrorToast } from './CommandBar';
import { CommandPalette } from './CommandPalette';
import { SmartBar } from './SmartBar';
import { CaptureComposer } from './CaptureComposer';
import { AskSheet } from '@/features/ai/AskSheet';
import { ShortcutsHelpDialog } from './ShortcutsHelpDialog';
import { Topbar } from './Topbar';
import { DayStripTopbar } from './DayStripTopbar';
import { TaskSlideOver } from '@/features/editor/TaskSlideOver';
import { CompleteRing } from '@/features/lifecycle/CompleteRing';
import { HabitDetailSheet } from '@/components/habits/HabitDetailSheet';
import { RoutineCompressionDialogConnector } from '@/components/routines/RoutineCompressionDialogConnector';
import { useStatusBarEffect } from '@/hooks/useStatusBarEffect';
import { useKeyboardEffect } from '@/hooks/useKeyboardEffect';
import { useAppLifecycle } from '@/hooks/useAppLifecycle';
import { setNotificationNavigator } from '@/features/notifications/notificationActions';
import { OfflineBanner } from './OfflineBanner';
import { SignInNudgeBanner } from './SignInNudgeBanner';
import { BillingIssueBanner } from './BillingIssueBanner';

interface RouteHandle {
  topbar?: 'day-strip';
  title?: string;
  chrome?: 'none';
}

const TITLES: Record<string, string> = {
  '/app': 'Today',
  '/app/inbox': 'Inbox',
  '/app/routines': 'Routines',
  '/app/habits': 'Habits',
  '/app/calendar': 'Calendar',
  '/app/settings': 'Settings',
};

export function AppShell() {
  useAuth();
  useKeyboardShortcuts();
  useStatusBarEffect();
  useKeyboardEffect();
  useAppLifecycle();
  const navigate = useNavigate();
  useEffect(() => {
    setNotificationNavigator(navigate);
  }, [navigate]);
  const bp = useBreakpoint();
  const isAtLeastMd = useIsAtLeast('md');
  const isAtLeastXl = useIsAtLeast('xl');
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const compressionRoutineId = useUIStore((s) => s.compressionDialogRoutineId);
  const closeCompression = useUIStore((s) => s.closeCompressionDialog);
  const { capture } = useCapture();

  const matches = useMatches();
  const leaf = matches[matches.length - 1];
  const handle = (leaf?.handle as RouteHandle | undefined) ?? {};
  const noChrome = handle.chrome === 'none';
  // Index routes under "/app" report a trailing slash (e.g. "/app/"); strip it
  // so TITLES / showFab lookups match the canonical "/app" path.
  const rawPathname = leaf?.pathname ?? '/';
  const pathname = rawPathname.length > 1 ? rawPathname.replace(/\/+$/, '') : rawPathname;
  const isDayStripRoute = handle.topbar === 'day-strip';
  const title = TITLES[pathname] ?? 'Brainday';

  const commandPaletteOpen = useUIStore((s) => s.commandPaletteOpen);
  const [mobileCaptureOpen, setMobileCaptureOpen] = useState(false);

  // Universal Links (https://brainday.app/e/*) — cold and warm starts. Gated
  // on hydration so a cold-start /e/build doesn't run the day builder against
  // still-empty stores (the Preferences rehydrate is async on native).
  const hydrated = useHydration();
  const { run: runDayBuild } = useDayBuilder();
  const deepLinkBuild = useCallback(() => {
    void runDayBuild().catch((e: unknown) => {
      if (e instanceof BraindayError) dayBuilderErrorToast(e);
    });
  }, [runDayBuild]);
  useEffect(() => {
    if (!hydrated) return;
    return initDeepLinks({
      navigate,
      openCapture: () => setMobileCaptureOpen(true),
      runBuild: deepLinkBuild,
    });
  }, [hydrated, navigate, deepLinkBuild]);
  // One docked entry bar for capture AND Ask — Today and Inbox only, where
  // the user is acting on tasks. Device feedback (2026-07-04) confirmed the
  // original FAB-era finding: an add-task affordance on Habits/Routines/
  // Settings is noise even when labeled (spec 2026-07-03-smart-bar-capture-ask.md).
  const showSmartBar =
    !isAtLeastMd &&
    (pathname === '/app' || pathname === '/app/inbox') &&
    !commandPaletteOpen &&
    !mobileCaptureOpen;

  // Force-collapse sidebar at md (between 768 and 1023); user toggle controls lg+.
  const sidebarIsCollapsed = bp === 'md' ? true : sidebarCollapsed;

  if (noChrome) {
    return (
      <div className="min-h-dvh bg-bg text-t1">
        <Outlet />
      </div>
    );
  }

  return (
    <div
      className="flex h-dvh w-full flex-col bg-bg pt-[env(safe-area-inset-top)] text-t1"
      data-breakpoint={bp}
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-overlay focus:rounded-md focus:bg-card focus:px-3 focus:py-1.5 focus:text-body focus:font-medium focus:text-t1 focus:shadow-window focus:outline-none focus:ring-2 focus:ring-brand"
      >
        Skip to main content
      </a>
      {/* SPA route changes are otherwise silent to screen readers — announce
          the destination page. Mobile especially: no Topbar h1 exists there. */}
      <div aria-live="polite" role="status" className="sr-only">
        {title}
      </div>
      <OfflineBanner />
      <SignInNudgeBanner />
      <BillingIssueBanner />
      <div className="flex flex-1 overflow-hidden">
        {isAtLeastMd && (
          <div className={cn('shrink-0', sidebarIsCollapsed ? 'w-[60px]' : 'w-[220px]')}>
            <Sidebar collapsed={sidebarIsCollapsed} onCapture={capture} />
          </div>
        )}

        <main id="main" className="flex flex-1 flex-col overflow-hidden">
          {isAtLeastMd && (isDayStripRoute ? <DayStripTopbar /> : <Topbar title={title} />)}
          <div className="flex flex-1 overflow-hidden">
            <div className="flex flex-1 flex-col overflow-hidden">
              <Outlet />
              {isAtLeastMd && isDayStripRoute && <CommandBar />}
            </div>
          </div>
        </main>

        {isAtLeastXl && <Narrator />}
      </div>

      {!isAtLeastMd && <BottomTabs />}
      {showSmartBar && <SmartBar onOpenCapture={() => setMobileCaptureOpen(true)} />}

      {!isAtLeastMd && (
        <CaptureComposer open={mobileCaptureOpen} onOpenChange={setMobileCaptureOpen} />
      )}
      {isAtLeastMd ? <CommandPalette /> : <AskSheet />}
      <ShortcutsHelpDialog />
      <TaskSlideOver />
      <CompleteRing />
      <HabitDetailSheet />
      {compressionRoutineId ? (
        <RoutineCompressionDialogConnector
          routineId={compressionRoutineId}
          onClose={closeCompression}
        />
      ) : null}
    </div>
  );
}
